<template>
    <Teleport to="body">
        <div
            v-if="visible"
            class="authenticator-setup"
            :class="{ dark }"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
        >
            <div class="authenticator-setup__backdrop"></div>
            <div class="authenticator-setup__card">
                <div class="authenticator-setup__body">
                    <h2 :id="titleId" class="authenticator-setup__title">
                        {{ $t('student.authenticator.title') }}
                    </h2>
                    <p class="authenticator-setup__lead">
                        {{ $t('student.authenticator.lead') }}
                    </p>
                    <ol class="authenticator-setup__steps">
                        <li>{{ $t('student.authenticator.stepApp') }}</li>
                        <li>{{ $t('student.authenticator.stepAdd') }}</li>
                        <li>{{ $t('student.authenticator.stepScan') }}</li>
                    </ol>
                    <div class="authenticator-setup__qr" v-if="qrDataUrl">
                        <img :src="qrDataUrl" :alt="$t('student.authenticator.qrAlt')" />
                    </div>
                    <label class="authenticator-setup__label" for="authenticator-setup-code">
                        {{ $t('student.authenticator.code') }}
                    </label>
                    <input
                        id="authenticator-setup-code"
                        v-model="code"
                        class="authenticator-setup__input"
                        type="text"
                        inputmode="numeric"
                        maxlength="6"
                        autocomplete="one-time-code"
                        @input="onCodeInput"
                    />
                    <p v-if="error" class="authenticator-setup__error">{{ error }}</p>
                    <button
                        type="button"
                        class="authenticator-setup__confirm"
                        :disabled="busy"
                        @click="confirm"
                    >
                        {{ busy ? $t('student.authenticator.confirming') : $t('student.authenticator.confirm') }}
                    </button>
                </div>
                <button type="button" class="authenticator-setup__close" @click="close">
                    {{ $t('student.authenticator.close') }}
                </button>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { mapState } from 'vuex';
import QRCode from 'qrcode';
import securityApi from '@/utils/portal/security';

export default {
    props: {
        dark: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            visible: false,
            qrDataUrl: '',
            code: '',
            busy: false,
            error: '',
            titleId: 'authenticator-setup-title',
        };
    },
    computed: {
        ...mapState(['token']),
    },
    mounted() {
        this.loadStatus();
    },
    beforeUnmount() {
        document.body.style.overflow = '';
    },
    methods: {
        async loadStatus() {
            if (!this.token) return;
            try {
                const status = await securityApi.authenticatorStatus(this.token);
                if (!status.required || status.configured || !status.otpauthUri) return;
                this.qrDataUrl = await QRCode.toDataURL(status.otpauthUri, {
                    width: 220,
                    margin: 1,
                    errorCorrectionLevel: 'M',
                });
                this.visible = true;
                document.body.style.overflow = 'hidden';
            } catch (error) {
                console.error('Erro ao carregar o status do Google Authenticator', error);
            }
        },
        onCodeInput(event) {
            this.code = String(event.target.value || '').replace(/\D/g, '').slice(0, 6);
            this.error = '';
        },
        async confirm() {
            if (this.busy || this.code.length !== 6) {
                this.error = this.$t('student.authenticator.invalid');
                return;
            }
            this.busy = true;
            this.error = '';
            try {
                const status = await securityApi.confirmAuthenticator(this.token, this.code);
                if (status.configured || !status.required) {
                    this.close();
                }
            } catch (error) {
                this.error = this.$t('student.authenticator.invalid');
                console.error('Erro ao confirmar o Google Authenticator', error);
            } finally {
                this.busy = false;
            }
        },
        close() {
            this.visible = false;
            document.body.style.overflow = '';
        },
    },
};
</script>

<style scoped>
.authenticator-setup {
    position: fixed;
    inset: 0;
    z-index: 4000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem;
}

.authenticator-setup__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(17, 24, 39, 0.55);
}

.authenticator-setup__card {
    position: relative;
    width: 100%;
    max-width: 26rem;
    max-height: calc(100dvh - 2.5rem);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
    color: #111827;
    border-radius: 0.75rem;
    padding: 1.5rem 1.5rem 1.25rem;
    box-shadow: 0 1.25rem 3rem rgba(15, 23, 42, 0.2);
}

.authenticator-setup__body {
    overflow: auto;
    min-height: 0;
}

.authenticator-setup.dark .authenticator-setup__card {
    background: #1a191f;
    color: #f3f4f6;
}

.authenticator-setup__title {
    margin: 0 0 0.5rem;
    font-size: 1.35rem;
    line-height: 1.3;
    text-align: center;
}

.authenticator-setup__lead {
    margin: 0 0 1rem;
    color: #6b7280;
    text-align: center;
    font-size: 0.95rem;
    line-height: 1.45;
}

.authenticator-setup.dark .authenticator-setup__lead {
    color: #9ca3af;
}

.authenticator-setup__steps {
    margin: 0 0 1.25rem;
    padding-left: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    font-size: 0.95rem;
    line-height: 1.4;
    text-align: left;
}

.authenticator-setup__qr {
    display: flex;
    justify-content: center;
    margin-bottom: 1.25rem;
}

.authenticator-setup__qr img {
    width: 11rem;
    height: 11rem;
    background: #fff;
    border-radius: 0.5rem;
    padding: 0.35rem;
}

.authenticator-setup__label {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.95rem;
    font-weight: 600;
    text-align: left;
}

.authenticator-setup__input {
    width: 100%;
    box-sizing: border-box;
    margin-top: 0.4rem;
    padding: 0.7rem 0.8rem;
    border: 1px solid #d0d5dd;
    border-radius: 0.375rem;
    font-size: 1.15rem;
    letter-spacing: 0.3em;
    text-align: center;
}

.authenticator-setup__error {
    margin: 0.6rem 0 0;
    color: #b42318;
    font-size: 0.85rem;
    text-align: left;
}

.authenticator-setup__confirm {
    width: 100%;
    margin-top: 0.9rem;
    border: none;
    border-radius: 0.375rem;
    padding: 0.8rem 1rem;
    background: #160266;
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
}

.authenticator-setup__confirm:disabled {
    opacity: 0.7;
    cursor: default;
}

.authenticator-setup__close {
    width: 100%;
    flex: 0 0 auto;
    margin-top: 0.75rem;
    border: none;
    border-radius: 0.375rem;
    padding: 0.8rem 1rem;
    background: #3db8bf;
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
}

.authenticator-setup__close:hover {
    background: #2a9ca3;
}
</style>
